import type { AuditDetails, Entity } from '../entity';
import { WebhookEventName } from '../webhooks-v2/webhook.entity.type';
import type { WorkflowDefinition } from './workflow.definition';

/**
 * A tenant-authored extension definition: an AI agent or a deterministic workflow. A definition belongs to its tenant database; it is independent of the OAuth runtime identity.
 * Every kind shares the revision, enablement and trigger rules and is dispatched through the same harness pipeline; `kind` never changes after create.
 */
export interface AgentDefinition extends Entity, AuditDetails {
    name: string;
    kind: ExtensionKind;
    contextKey: ExtensionAgentContextKey;
    /** The agent's instructions; for a workflow, an optional description. */
    instructions: string;
    /** The steps a workflow runs; present exactly when `kind` is `Workflow`. */
    workflow?: WorkflowDefinition;
    enabled: boolean;
    triggers: ExtensionAgentTrigger[];
    revision: number;
}

/**
 * What an extension is. Agents and workflows are customer-authored data run by the `entity.agent` and `entity.workflow` runtime cells.
 * Code extensions are WorkBuddy-authored cells and are never accepted from a customer save.
 */
export enum ExtensionKind {
    Agent = 'agent',
    Workflow = 'workflow',
    Code = 'code',
}

export enum ExtensionAgentContextKey {
    Job = 'job',
}

export enum ExtensionAgentTrigger {
    Created = 'created',
    Updated = 'updated',
    StatusChanged = 'status_changed',
}

export const EXTENSION_AGENT_TRIGGER_EVENTS: Record<ExtensionAgentTrigger, string> = {
    [ExtensionAgentTrigger.Created]: WebhookEventName.JobCreated,
    [ExtensionAgentTrigger.Updated]: WebhookEventName.JobUpdated,
    [ExtensionAgentTrigger.StatusChanged]: WebhookEventName.JobStatusChanged,
};

export interface SaveAgentDefinitionRequest {
    _id: string;
    name: string;
    contextKey: ExtensionAgentContextKey;
    kind: ExtensionKind;
    instructions: string;
    workflow?: WorkflowDefinition;
    enabled: boolean;
    triggers: ExtensionAgentTrigger[];
    revision?: number;
}

export interface GetAgentDefinitionsRequest {
    search?: string;
    contextKey?: ExtensionAgentContextKey;
    /** Narrows the page and its total to one kind; omitted lists every kind. */
    kind?: ExtensionKind;
    page?: number;
    pageSize?: number;
}

export interface AgentDefinitionsPage {
    items: AgentDefinition[];
    total: number;
    page: number;
    pageSize: number;
}

export enum ExtensionAgentSocketSubject {
    Capabilities = 'GetExtensionAgentCapabilitiesQuery',
    List = 'GetExtensionAgentsQuery',
    Get = 'GetExtensionAgentQuery',
    Save = 'SaveExtensionAgentCommand',
    Delete = 'DeleteExtensionAgentCommand',
    Assignments = 'GetExtensionAgentAssignmentsQuery',
    SetAssignment = 'SetExtensionAgentAssignmentCommand',
    RunNow = 'RunExtensionAgentNowCommand',
    SampleData = 'GetWorkflowSampleDataQuery',
    EntityCatalogue = 'GetWorkflowEntityCatalogueQuery',
    TestStep = 'TestWorkflowStepCommand',
    TestWorkflow = 'TestWorkflowCommand',
}
